<!-- last modified by AI tools on 2026-10-05 -->
<script setup lang="ts">
import { computed } from 'vue';
import { FluidityModule } from '@ibgroup-tws-webapps/fluidity';
import { USE_MOCK } from '@/constants';
import { useUserState } from '@/stores/useUserState';

interface Props {
  seriesColors?: string[];
  chartType?: 'Line' | 'Area';
  conids?: string[];
  chartHeight?: number;
  period?: string;
  step?: string;
  seriesLabels?: Record<string, string>;
  backgroundColor?: string;
  /** Show each line's axis tag (short title + latest value). */
  showLabels?: boolean;
  /** Hide mini.chart's built-in no-data legend when the parent lists no-data series itself. */
  hideNoDataLegend?: boolean;
}

/** Payload of mini.chart's noDataSeriesChange event. */
export interface NoDataSeries {
  conid: string;
  name: string;
  color: string;
}

const props = withDefaults(defineProps<Props>(), {
  chartType: 'Line',
  conids: () => [], // Empty array as default - parent must provide conids
  chartHeight: 280,
  showLabels: true,
  hideNoDataLegend: false,
});

const emit = defineEmits<{
  /** Series that settled with nothing to plot. */
  noDataSeriesChange: [series: NoDataSeries[]];
}>();

const userState = useUserState();
// Add validation for empty conids array with fallback
const validConids = computed(() => {
  return props.conids.length > 0 ? props.conids : ['827714781', '827714785']; // Fallback for development
});

// FluidityModule types relayed module events as unknown[]
const onNoDataSeriesChange = (...args: unknown[]) => {
  emit('noDataSeriesChange', (args[0] as NoDataSeries[] | undefined) ?? []);
};
</script>

<template>
  <div class="w-full h-full flex flex-col">
    <div class="election-chart-wrapper w-full flex-1 min-h-0 relative">
      <FluidityModule
        mount-module-key="ModuleMultiSeriesChart"
        moduleName="mini.chart"
        :module-props="{
          conids: validConids,
          mock: USE_MOCK,
          chartType: props.chartType,
          seriesColors: props.seriesColors,
          useCustomColors: !!props.seriesColors,
          backgroundColor: props.backgroundColor ?? 'bg-(--p-dialog-background)',
          chartHeight: props.chartHeight,
          period: props.period,
          step: props.step,
          seriesLabels: props.seriesLabels,
          authenticated: userState.authenticated,
          showLabels: props.showLabels,
          hideNoDataLegend: props.hideNoDataLegend,
        }"
        @no-data-series-change="onNoDataSeriesChange"
      >
      </FluidityModule>
    </div>
  </div>
</template>

<style scoped>
/* Force FluidityModule wrapper to fill the container */
.election-chart-wrapper :deep(> *) {
  width: 100% !important;
  height: 100% !important;
}

/* Only size the multi-series-chart root and its direct structural divs —
   do NOT target lightweight-charts internal divs */
.election-chart-wrapper :deep(.multi-series-chart),
.election-chart-wrapper :deep(.multi-series-chart > div),
.election-chart-wrapper :deep(.chart-wrapper),
.election-chart-wrapper :deep(.chart-container) {
  width: 100%;
  height: 100%;
}
</style>
